import React, {useEffect, useState} from "react";
import {Modal, Button} from "antd";
import {useTranslation} from "react-i18next";
import {normalizeServerImagesToStrings} from "../../../utils/ImageUtils/imageUrls"

const baseUrl = process.env.REACT_APP_API_URL || "";

/**
 * props:
 * - open: boolean
 * - images: string[] | {url:string}[]   // images array
 * - onClose: () => void
 * - onSave: (order: number[]) => void   // array index
 * - title?: string
 * - t?: (k:string, vars?:any) => string
 */
export default function ReorderImagesModal({open, images = [], onClose, onSave, title}) {
    const [order, setOrder] = useState([]);
    const [dragFrom, setDragFrom] = useState(null);
    const [dragOver, setDragOver] = useState(null);
    const {t} = useTranslation();

    useEffect(() => {
        if (open) setOrder([...Array(images.length).keys()]);
    }, [open, images.length]);

    const handleSave = () => {
        onSave?.(order);
    };

    const onDragStart = (idx) => (e) => {
        setDragFrom(idx);
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", String(idx));
        const imgEl = e.currentTarget.querySelector("img");
        if (imgEl) {
            const rect = imgEl.getBoundingClientRect();
            e.dataTransfer.setDragImage(imgEl, e.clientX - rect.left, e.clientY - rect.top);
        }
        e.currentTarget.classList.add("shadow-lg", "scale-[1.02]");
    };

    const onDragEnter = (idx) => (e) => {
        e.preventDefault();
        if (dragOver !== idx) setDragOver(idx);
    };
    const onDragOver = (e) => e.preventDefault();

    const onDragEnd = (e) => {
        e.currentTarget.classList.remove("shadow-lg", "scale-[1.02]");
        setDragFrom(null);
        setDragOver(null);
    };

    const onDrop = (toIdx) => (e) => {
        e.preventDefault();
        if (dragFrom === null || dragFrom === toIdx) return;
        setOrder((prev) => {
            const next = [...prev];
            [next[dragFrom], next[toIdx]] = [next[toIdx], next[dragFrom]];
            return next;
        });
        setDragFrom(null);
        setDragOver(null);
    };

    const shown = React.useMemo(
        () => (order.length ? order.map(i => images[i]) : images),
        [order, images]
    );
    const urls = React.useMemo(
        () => normalizeServerImagesToStrings(shown, baseUrl),
        [shown]
    );

    const label = t("postPage.cards.orderLabel");
    const titleText = title ?? t("postPage.cards.reorderModalTitle");

    return (
        <Modal
            open={open}
            onCancel={onClose}
            title={titleText}
            width={900}
            footer={[
                <Button key="cancel" onClick={onClose}>
                    {t("postPage.buttons.cancel")}
                </Button>,
                <Button key="save" type="primary" onClick={handleSave} disabled={!images.length}>
                    {t("postPage.buttons.save")}
                </Button>,
            ]}
        >
            <div className="mb-2 text-sm text-gray-500">{label}</div>

            {/*{grid preview}*/}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {(order.length ? order.map((i) => images[i]) : images).map((img, viewIdx) => {
                    const originalIdx = order[viewIdx] ?? viewIdx;
                    const url = typeof img === "string" ? img : (img?.url || img);

                    return (
                        <div
                            key={originalIdx}
                            draggable
                            onDragStart={onDragStart(viewIdx)}
                            onDragEnter={onDragEnter(viewIdx)}
                            onDragOver={onDragOver}
                            onDrop={onDrop(viewIdx)}
                            onDragEnd={onDragEnd}
                            className={`relative rounded-xl overflow-hidden border transition
                         ${dragOver === viewIdx ? "border-violet-500 ring-2 ring-violet-300" : "border-gray-200"}
                         cursor-grab bg-white`}
                        >
                            {/* number badge */}
                            <div className="absolute top-2 left-2 z-10 px-2 py-0.5 text-xs rounded-full
                              bg-black/70 text-white">
                                {viewIdx + 1}
                            </div>

                            <img
                                src={urls[viewIdx]}
                                alt={`image-${originalIdx}`}
                                className="block w-full h-36 object-cover select-none"
                                draggable={false}
                            />

                            <div className="absolute bottom-2 right-2 text-xs px-2 py-1 rounded bg-white/90 border">
                                ⇅ drag
                            </div>
                        </div>
                    );
                })}
            </div>
        </Modal>
    );
}
